/**
 * Environment options for Tell Money consents
 */
export enum TellMoneyEnvironment {
  SANDBOX = 'sandbox',
  INTEGRATION = 'integration',
  PRODUCTION = 'production',
}

/**
 * Consent status options
 */
export enum TellMoneyConsentStatus {
  AWAITING_AUTHORISATION = 'AwaitingAuthorisation',
  AWAITING_EXCHANGE = 'AwaitingExchange',
  AUTHORISATION_DECLINED = 'AuthorisationDeclined',
  AUTHORISED = 'Authorised',
  REVOKED = 'Revoked',
  EXPIRED = 'Expired',
  CONSUMED = 'Consumed',
}

/**
 * Consent scope options
 */
export enum TellMoneyConsentScope {
  ACCOUNTS = 'accounts',
  PAYMENTS = 'payments',
  FUNDS_CONFIRMATIONS = 'fundsconfirmations',
}

/**
 * Payment type options for OBIE
 */
export enum TellMoneyOBIEPaymentType {
  DOMESTIC = 'Domestic',
  DOMESTIC_SCHEDULED = 'Domestic Scheduled',
  DOMESTIC_STANDING_ORDER = 'Domestic Standing Order',
  INTERNATIONAL = 'International',
  INTERNATIONAL_SCHEDULED = 'International Scheduled',
  INTERNATIONAL_STANDING_ORDER = 'International Standing Order',
}

/**
 * Payment type options for NextGenPSD2
 */
export enum TellMoneyNextGenPSD2PaymentType {
  SEPA_CREDIT_TRANSFERS = 'SEPA Credit Transfers',
  INSTANT_SEPA_CREDIT_TRANSFERS = 'Instant SEPA Credit Transfers',
  TARGET_2_PAYMENTS = 'Target 2 Payments',
  CROSS_BORDER_CREDIT_TRANSFERS = 'Cross Border Credit Transfers',
}

/**
 * OBIE Permission options
 */
export enum TellMoneyOBIEPermission {
  READ_ACCOUNTS_BASIC = 'ReadAccountsBasic',
  READ_ACCOUNTS_DETAIL = 'ReadAccountsDetail',
  READ_BALANCES = 'ReadBalances',
  READ_BENEFICIARIES_BASIC = 'ReadBeneficiariesBasic',
  READ_BENEFICIARIES_DETAIL = 'ReadBeneficiariesDetail',
  READ_DIRECT_DEBITS = 'ReadDirectDebits',
  READ_SCHEDULED_PAYMENTS_BASIC = 'ReadScheduledPaymentsBasic',
  READ_SCHEDULED_PAYMENTS_DETAIL = 'ReadScheduledPaymentsDetail',
  READ_TRANSACTIONS_BASIC = 'ReadTransactionsBasic',
  READ_TRANSACTIONS_CREDITS = 'ReadTransactionsCredits',
  READ_TRANSACTIONS_DEBITS = 'ReadTransactionsDebits',
  READ_TRANSACTIONS_DETAIL = 'ReadTransactionsDetail',
  READ_STATEMENTS_BASIC = 'ReadStatementsBasic',
  READ_STATEMENTS_DETAIL = 'ReadStatementsDetail',
}

/**
 * NextGenPSD2 Account Type options
 */
export enum TellMoneyNextGenPSD2AccountType {
  IBAN = 'iban',
  BBAN = 'bban',
  MSISDN = 'msisdn',
}

/**
 * NextGenPSD2 Permission Level options
 */
export enum TellMoneyNextGenPSD2PermissionLevel {
  ACCOUNTS = 'accounts',
  TRANSACTIONS = 'transactions',
  BALANCES = 'balances',
}

/**
 * NextGenPSD2 Account Permission
 */
export interface TellMoneyNextGenPSD2AccountPermission {
  AccountId: string;
  AccountType: TellMoneyNextGenPSD2AccountType;
  Currency?: string; // ISO 4217 Alpha-3
}

/**
 * NextGenPSD2 Permissions
 */
export interface TellMoneyNextGenPSD2Permissions {
  accounts?: TellMoneyNextGenPSD2AccountPermission[];
  transactions?: TellMoneyNextGenPSD2AccountPermission[];
  balances?: TellMoneyNextGenPSD2AccountPermission[];
}

/**
 * Payment information for payment consents
 */
export interface TellMoneyPaymentInformation {
  amount: number;
  currency: string;
  reference?: string;
  description?: string;
  beneficiary?: {
    name: string;
    account_number?: string;
    sort_code?: string;
    iban?: string;
  };
  schedule?: {
    frequency: 'one-off' | 'weekly' | 'monthly' | 'yearly';
    start_date: string;
    end_date?: string;
  };
  // Additional payment-specific fields may be included
}

/**
 * Tell Money Consent response
 */
export interface TellMoneyConsent {
  /** ConsentId as per the requested resource */
  ConsentId: string;

  /** Indicates the environment in which the Consent was created */
  Environment: TellMoneyEnvironment;

  /** UUID of your Gateway */
  GatewayId: string;

  /** Domain name of your Gateway */
  GatewayDomain: 'seapoint-uk' | 'seapoint-eu';

  /** Name of the TPP through which the Consent originated */
  TppName: string;

  /** Website of the TPP through which the Consent originated */
  TppWebsite: string;

  /** Name of the TPP's App – a TPP may have multiple apps */
  AppName: string;

  /** Description of the TPP's App */
  AppDescription: string;

  /** Website of the TPP's App */
  AppWebsite: string;

  /** Status of the Consent */
  ConsentStatus: TellMoneyConsentStatus;

  /** Scope of the Consent (accounts, payments, fundsconfirmations) */
  Scope: TellMoneyConsentScope;

  /** Array of Account IDs associated with the consent. Null until consent is authorised. */
  ConsentedAccounts: string[] | null;

  /** User ID associated with the consent. Null until consent is authorised. */
  ConsentedUserId: string | null;

  /** For payments, indicates the type of payment being consented to */
  PaymentType?: string;

  /** For accounts with OBIE or NextGenPSD2, permissions that the Consent relates to */
  Permissions?: TellMoneyOBIEPermission[] | TellMoneyNextGenPSD2Permissions;

  /** For payments, contains details of the payment being requested */
  PaymentInformation?: TellMoneyPaymentInformation;

  /** Date & time (UTC) that the consent request was created */
  ConsentRequestCreated: string;

  /** Date & time (UTC) that the consent request expires (or expired) */
  ConsentRequestExpiry: string;

  /** Date & time (UTC) that the consent was granted (null before SCA) */
  ConsentGranted: string | null;

  /** Date & time (UTC) that the consent expires / expired (null before SCA) */
  ConsentExpiry: string | null;

  /** Date & time (UTC) that the consent was revoked (null before SCA or if not revoked) */
  ConsentRevoked: string | null;
}

/**
 * Request payload for creating a new consent
 */
export interface CreateConsentRequest {
  /** Unique identifier for the consent */
  ConsentId: string;

  /** Indicates if the account holder authentication has been successful and the consent has been accepted (“Pass”) or if either the authentication has failed, or the account holder has rejected the consent request (“Fail”) */
  Outcome: 'Pass' | 'Fail';

  /** A unique ID for your account holder. This may not be known to the account holder. This is the ID we will use in future interaction to request access to resources or to initiate a payment */
  UserId: string;

  /** An array of AccountIds which the account holder has approved for the consent. Mandatory if Outcome is Pass */
  Accounts: string[];

  /** (Mandatory if using NextGenPSD2) An array of resource IDs identifiable to yourselves, matched against the account identifiers that the PSU has provided consent for */
  AccountIdMap?: {
    /** Unique account identifier known to the ASPSP */
    ResourceId: string;

    /** If currency is provided in consent, then provide currency expressed in ISO 4217 three-letter code format */
    Currency?: string;

    /** The type of account */
    AccountType: 'iban' | 'bban' | 'msisdn';

    /** The public account identifier know to the PSU and TPP (the identifier must match the AccountType provided)*/
    AccountId: string;
  }[];
}

export interface CreateConsentResponse {
  /** Location to redirect the account holder to */
  Location?: string;
}
